import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Router } from 'react-router-dom'
import * as Sentry from '@sentry/browser'
import { setupI18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { ErrorBoundary } from '@sentry/react'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import Tracker from '@openreplay/tracker'

import { Provider as ReduxProvider } from 'react-redux'
import { PrismicProvider } from '@prismicio/react'
import { HelmetProvider } from 'react-helmet-async'
import { SignupProvider } from 'contexts/SignupContext'
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { UnleashProvider } from 'modules/FeatureFlags/Unleash/UnleashProvider'
import { sanitizer } from 'modules/OpenReplay'
import { DimensionsProvider, ThemeProvider } from '@telavita-core/react-design-kit'
import { ToastContainer } from '@telavita-core/react-design-kit'

import { client } from 'settings/prismic'
import { initializeSentry } from 'settings/sentry'
import { initializeMixPanel } from 'settings/mixpanel'
import { initializeOctadeskChat } from 'settings/octadesk'

import LangPT from './locales/pt/messages.js'
import LangEN from './locales/en/messages.js'

import CustomError from 'views/CustomError'
import ModalPortal from './containers/ModalPortal'
import { Spinner } from './components'

import AppRouter, { history } from './routers/AppRouter'
import { getAuthToken } from 'utils/authHelper'

import { authUser } from './redux/actions'
import { configureStore } from 'redux/cofigureStore'
import { AUTH_CHANGE_LOADING } from 'redux/actions/_actionTypes.js'

import 'normalize-css/normalize.css'
import 'react-phone-number-input/style.css'

import './app.sass'
import './static/styles/styles.sass'

export const lang = {
  pt_br: LangPT,
  en: LangEN
}

export const store = configureStore()

const tracker = new Tracker({
  projectKey: process.env.REACT_APP_OPEN_REPLAY_PROJECT_KEY,
  ingestPoint: 'https://openreplay.telavita.com.br/ingest',
  __DISABLE_SECURE_MODE: process.env.NODE_ENV === 'development',
  __debug__: process.env.NODE_ENV === 'development',
  network: {
    capturePayload: true,
    sanitizer: sanitizer,
  },
})
tracker.start()

const MonitoringWrapper = () => {
  const userData = useSelector(state => state.auth.userData)

  useEffect(() => {
    if (!userData) return

    Sentry.setUser({
      id: userData.session.sub,
      username: userData.session.username,
    })
    tracker.setUserID(userData.session.username)
  }, [userData])

  return null
}

const App = () => {
  const i18n = setupI18n()
  const dispatch = useDispatch()
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 60 * 1000 * 60, // 1 hora
      }
    }
  })

  const isLoading = useSelector(state => state.auth.isLoading)

  useEffect(() => {
    initializeMixPanel(process.env.REACT_APP_MIXPANEL_TOKEN)
    initializeSentry({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      release: process.env.REACT_APP_VERSION,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    })
    initializeOctadeskChat()
  }, [])

  useEffect(() => {
    const userToken = getAuthToken()
    if (userToken) {
      dispatch(authUser(userToken))
    } else {
      dispatch({ type: AUTH_CHANGE_LOADING, payload: { isLoading: false } })
    }
  }, [dispatch])

  return (
    <Router history={history}>
      <MonitoringWrapper />
      <ThemeProvider>
        <UnleashProvider>
          <ErrorBoundary
            // Por renderizar o `CustomError` o `ErrorBoundary` depende do
            // `Router`, `ThemeProvider` e `UnleashProvider`.
            showDialog={true}
            fallback={
              <CustomError
                title="Houve algo de errado."
                subtitle="Nosso time já foi notificado e está trabalhando para resolver o seu problema, nos desculpamos pelo inconveniente."
              />
            }
            dialogOptions={{
              title: 'Hmm, houve algo de errado...',
              subtitle: 'Se quiser ajudar nosso time a identificar esse erro, conte abaixo o que aconteceu.',
              subtitle2: '',
              labelSubmit: 'Enviar mensagem',
              labelComments: 'Digite aqui'
            }}
          >
            <DimensionsProvider>
              {isLoading
                ? <Spinner />
                : <GTMProvider state={{ id: process.env.REACT_APP_GTM_ID }}>
                  <HelmetProvider>
                    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY} useEnterprise={true}>
                      <PrismicProvider client={client}>
                        <I18nProvider i18n={i18n} language='pt_br' catalogs={lang}>
                          <QueryClientProvider client={queryClient}>
                            <SignupProvider>
                              <ModalPortal />
                              <ToastContainer
                                newestOnTop
                                theme='colored'
                              />
                              <AppRouter />
                            </SignupProvider>
                          </QueryClientProvider>
                        </I18nProvider>
                      </PrismicProvider>
                    </GoogleReCaptchaProvider>
                  </HelmetProvider>
                </GTMProvider>
              }
            </DimensionsProvider>
          </ErrorBoundary>
        </UnleashProvider>
      </ThemeProvider>
    </Router>
  )
}

const AppWithRedux = () => {
  return (
    <ReduxProvider store={store}>
      <App />
    </ReduxProvider>
  )
}

export default AppWithRedux
